import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, CheckCircle, Brain, Lightbulb, Target, Compass, Quote, Users, Star } from 'lucide-react';

const LuminarcPage = () => {
  const outcomes = [
    {
      icon: Brain,
      title: 'Structured Thinking Clarity',
      description: 'Develop frameworks that cut through complexity and help you see patterns and possibilities others miss.',
    },
    {
      icon: Lightbulb,
      title: 'Powerful Storytelling',
      description: 'Master the art of narrative to inspire action, align teams, and influence stakeholders effectively.',
    },
    {
      icon: Target,
      title: 'Decision-Making in Uncertainty',
      description: 'Build confidence in making high-stakes decisions when data alone isn\'t enough to guide you.',
    },
    {
      icon: Compass,
      title: 'Navigate AI Disruption',
      description: 'Understand how to lead effectively when machines can process faster than humans ever could.',
    },
  ];

  const principles = [
    { text: 'Non-technical and hands-on', detail: 'No coding or tech background required' },
    { text: 'Intimate cohort experience', detail: 'Limited seats for deep engagement' },
    { text: 'Focus on narrative, not numbers', detail: 'Human-centric approach' },
    { text: 'Real-world application', detail: 'Immediately applicable insights' },
    { text: 'Post-program community', detail: 'Ongoing peer support network' },
    { text: 'Transformational, not informational', detail: 'Change how you think' },
  ];

  const testimonials = [
    {
      quote: "Luminarc™ transformed how I approach every strategic decision. The narrative thinking framework gave our entire leadership team a new lens.",
    },
    {
      quote: "As a student, I wasn't sure if this was for me. But the frameworks I learned have already helped me stand out in interviews and think differently about my career.",
    },
    {
      quote: "The best investment I've made in my professional development. Period.",
    },
  ];

  return (
    <main className="pt-20">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-brand-charcoal overflow-hidden" data-testid="luminarc-hero">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,rgba(196,158,77,0.2),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_80%,rgba(92,45,145,0.3),transparent_50%)]" />
          <motion.div
            animate={{ 
              backgroundPosition: ['0% 0%', '100% 100%'],
            }}
            transition={{ duration: 20, repeat: Infinity, repeatType: 'reverse' }}
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.1"%3E%3Cpath d="m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              backgroundSize: '60px 60px',
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/30 rounded-full px-4 py-2 mb-8"
            >
              <Sparkles className="w-4 h-4 text-brand-gold" />
              <span className="text-brand-gold text-sm font-medium">Flagship Experience</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-heading text-6xl sm:text-7xl lg:text-8xl font-bold text-white mb-8"
            >
              Luminarc<span className="text-brand-gold">™</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-3xl sm:text-4xl text-brand-gold-lite font-light italic mb-8"
            >
              "Where logic ends, perspective begins."
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-xl text-gray-300 mb-8 leading-relaxed max-w-2xl"
            >
              A transformational, hands-on experience designed to develop{' '}
              <span className="text-white font-semibold">narrative thinking</span> in the age of AI. 
              Not a workshop—a paradigm shift for those who need to communicate, decide, and 
              inspire when data alone isn't enough.
            </motion.p>

            {/* Audience badges */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-wrap gap-2 mb-10"
            >
              {['CEOs & Founders', 'C-Suite', 'VPs & Directors', 'High-Potentials', 'Professionals', 'Students'].map((audience) => (
                <span key={audience} className="bg-white/10 text-gray-300 text-sm px-4 py-2 rounded-full border border-white/20 hover:bg-white/15 transition-colors">
                  {audience}
                </span>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 bg-brand-gold text-brand-charcoal font-semibold px-10 py-5 text-lg hover:bg-brand-gold-lite hover:scale-105 transition-all duration-300 group"
                data-testid="luminarc-hero-cta"
              >
                <span>Express Interest</span>
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex flex-col items-center gap-2"
          >
            <span className="text-gray-500 text-xs uppercase tracking-widest">Discover More</span>
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="w-1.5 h-1.5 bg-brand-gold rounded-full"
              />
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* What is Luminarc */}
      <section className="py-24 bg-brand-bg-light relative overflow-hidden" data-testid="luminarc-what">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-brand-lavender/50 to-transparent pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block bg-brand-purple/10 text-brand-purple text-sm font-semibold px-4 py-2 rounded-full mb-4">
                What is Luminarc™?
              </span>
              <h2 className="font-heading text-4xl sm:text-5xl font-bold text-brand-charcoal mb-6">
                Not a Workshop.{' '}
                <span className="text-brand-purple">A Transformation.</span>
              </h2>
              <div className="space-y-4 text-brand-body text-lg leading-relaxed">
                <p>
                  Luminarc™ is a category-defining experience that goes beyond traditional training. 
                  It's designed for anyone who recognizes that in an AI-driven world, 
                  the human capacity for narrative thinking becomes the ultimate competitive advantage.
                </p>
                <p>
                  While machines excel at processing data, humans alone possess the ability to 
                  weave meaning, context, and purpose into coherent stories that inspire action.
                </p>
                <p>
                  Luminarc™ develops this uniquely human capability through immersive exercises, 
                  reflective practices, and peer collaboration in an intimate cohort setting.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-brand-charcoal rounded-2xl p-8">
                <h3 className="font-heading text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <Sparkles className="w-6 h-6 text-brand-gold" />
                  Luminarc™ Principles
                </h3>
                <div className="space-y-4">
                  {principles.map((principle, index) => (
                    <motion.div 
                      key={index} 
                      className="flex items-start gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <CheckCircle className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-white font-medium">{principle.text}</span>
                        <p className="text-gray-400 text-sm">{principle.detail}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Expected Outcomes */}
      <section className="py-24 bg-white" data-testid="luminarc-outcomes">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block bg-brand-gold/10 text-brand-gold text-sm font-semibold px-4 py-2 rounded-full mb-4">
              Expected Outcomes
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-brand-charcoal mb-6">
              What You'll <span className="text-brand-purple">Gain</span>
            </h2>
            <p className="text-brand-muted text-lg max-w-2xl mx-auto">
              Luminarc™ participants emerge with new mental models and practical skills 
              that transform how they lead, communicate, and decide.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {outcomes.map((outcome, index) => (
              <motion.div
                key={outcome.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <div className="bg-brand-bg-light p-8 rounded-2xl h-full border border-transparent hover:border-brand-purple/20 hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-gradient-to-br from-brand-purple to-brand-purple-mid rounded-2xl flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                    <outcome.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-brand-charcoal mb-3">
                    {outcome.title}
                  </h3>
                  <p className="text-brand-muted leading-relaxed">
                    {outcome.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-24 bg-brand-charcoal relative overflow-hidden" data-testid="luminarc-audience">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(196,158,77,0.15),transparent_50%)]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/30 rounded-full px-4 py-2 mb-4">
                <Users className="w-4 h-4 text-brand-gold" />
                <span className="text-brand-gold text-sm font-medium">Who It's For</span>
              </span>
              <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
                For Leaders at{' '}
                <span className="text-brand-gold">Every Stage</span>
              </h2>
              <div className="space-y-4 text-gray-300 text-lg leading-relaxed mb-8">
                <p>
                  Luminarc™ is designed for anyone who faces complex decisions in uncertain environments—
                  from seasoned executives to emerging professionals and ambitious students.
                </p>
                <p>
                  If you find yourself asking "What story should we tell?" more often than 
                  "What does the data say?"—Luminarc™ is for you.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {['CEOs & Founders', 'C-Suite Executives', 'VPs & Directors', 'High-Potential Leaders', 'Professionals', 'Students'].map((role) => (
                  <motion.div 
                    key={role} 
                    className="flex items-center gap-3 bg-white/5 p-3 rounded-lg"
                    whileHover={{ scale: 1.02, backgroundColor: 'rgba(255,255,255,0.1)' }}
                  >
                    <CheckCircle className="w-5 h-5 text-brand-gold" />
                    <span className="text-white">{role}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=500&fit=crop"
                alt="Diverse professionals"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-brand-purple relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
              What Participants Say
            </h2>
            <p className="text-white/70 text-lg">
              Hear from those who've experienced the transformation
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8"
              >
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-5 h-5 text-brand-gold fill-brand-gold" />
                  ))}
                </div>
                <Quote className="w-8 h-8 text-brand-gold/30 mb-4" />
                <p className="text-white italic leading-relaxed mb-6">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="text-white font-semibold">Verified Client</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-brand-charcoal to-brand-purple relative overflow-hidden" data-testid="luminarc-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Sparkles className="w-16 h-16 text-brand-gold mx-auto mb-8" />
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to See Beyond the Data?
            </h2>
            <p className="text-white/80 text-xl mb-10 max-w-2xl mx-auto">
              Luminarc™ cohorts are limited to ensure an intimate, transformational experience. 
              Express your interest to learn about upcoming programs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-charcoal font-semibold px-12 py-5 text-lg hover:bg-brand-gold-lite hover:scale-105 transition-all duration-300 group"
                data-testid="luminarc-final-cta"
              >
                Express Interest
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white font-semibold px-12 py-5 text-lg hover:bg-white/10 hover:border-white/50 transition-all duration-300"
              >
                Explore Other Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default LuminarcPage;
